import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query'
import { retryRead } from '@shared/api/retry-read'
import { apiFetch } from '@shared/api/http'
import { toQueryString } from '@shared/api/query'
import { SaleDetailSchema, SalePageSchema } from './sale.contract'
import type { SaleDetail, SalePage, SalesQueryParams } from './sale.contract'

export type SalesFilters = Omit<SalesQueryParams, 'cursor'>

export const salesKeys = {
  list: (filters: SalesFilters) => ['sales', 'list', filters] as const,
  detail: (saleId: string) => ['sales', 'detail', saleId] as const,
}

export async function getSalesPage(params: SalesQueryParams & { dashboard?: boolean }): Promise<SalePage> {
  const query = toQueryString({
    dashboard: params.dashboard,
    cursor: params.cursor,
    limit: params.limit,
    status: params.status,
    productId: params.productId,
    currency: params.currency,
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
    q: params.q,
  })
  return SalePageSchema.parse(await apiFetch<unknown>(`/sales${query}`))
}

export function salesInfiniteQuery(filters: SalesFilters) {
  return infiniteQueryOptions({
    queryKey: salesKeys.list(filters),
    queryFn: ({ pageParam }) => getSalesPage({ ...filters, cursor: pageParam ?? undefined }),
    retry: retryRead,
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: SalePage) => lastPage.nextCursor,
    refetchInterval: (query) =>
      query.state.data?.pages.some((page) =>
        page.items.some((sale) => sale.status === 'REFUND_REQUESTED'),
      )
        ? 3000
        : false,
  })
}

export function useSalesInfiniteQuery(filters: SalesFilters, enabled = true) {
  return useInfiniteQuery({ ...salesInfiniteQuery(filters), enabled })
}

export async function getSaleDetail(
  saleId: string,
  scope: 'journey' | 'charge' = 'journey',
): Promise<SaleDetail> {
  return SaleDetailSchema.parse(
    await apiFetch<unknown>(
      `/sales/${encodeURIComponent(saleId)}${scope === 'charge' ? '?scope=charge' : ''}`,
    ),
  )
}

export function saleDetailQuery(saleId: string, scope: 'journey' | 'charge' = 'journey') {
  return queryOptions({
    queryKey: scope === 'charge' ? [...salesKeys.detail(saleId), scope] : salesKeys.detail(saleId),
    queryFn: () => getSaleDetail(saleId, scope),
    retry: retryRead,
    refetchInterval: (query) => {
      const sale = query.state.data
      return sale &&
        [sale, ...(sale.funnelStages ?? [])].some((charge) => charge.status === 'REFUND_REQUESTED')
        ? 3000
        : false
    },
  })
}

export function useSaleDetailQuery(saleId: string | null, scope: 'journey' | 'charge' = 'journey') {
  return useQuery({
    ...saleDetailQuery(saleId ?? '', scope),
    enabled: saleId !== null,
  })
}

export function useSaleChargesQuery(saleId: string) {
  return useInfiniteQuery({
    queryKey: ['sales', 'charges', saleId],
    queryFn: async ({ pageParam }) => {
      const query = toQueryString({ cursor: pageParam ?? undefined, limit: 30 })
      return SalePageSchema.parse(
        await apiFetch<unknown>(`/sales/${encodeURIComponent(saleId)}/charges${query}`),
      )
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: SalePage) => lastPage.nextCursor,
  })
}
